import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Input, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AsyncPipe, NgIf } from "@angular/common";
import { MapDisplaySearchComponent } from "../search-display/map-display/map-display-search.component";
import { GridDisplaySearchComponent } from "../search-display/grid-display/grid-display-search.component";
import { AiViewDisplayComponent } from "../search-display/ai-view-display/ai-view-display.component";
import { PropertyAnalystDisplayComponent } from "../search-display/property-analyst-display/property-analyst-display.component";
import { SearchQueryService } from "../services/search";
import { SearchDisplayMode } from "../model";
import { CuratorViewerService } from "../../viewer-components/services/internal-services/viewer-services/curator-viewer.service";
import { ToolbarService } from "../../viewer-components/services/internal-services/toolbar.service";
import { LoadingService } from "../../viewer-components/services/internal-services/loading.service";
import { MapService } from "../../viewer-components/services/internal-services/map.service";
import { UrlStateService } from "../../viewer-components/services/internal-services/url-state.service";
import { DomService } from "../../viewer-components/services/internal-services/dom.service";
import { FeaturesSelectedService } from "../../viewer-components/services/internal-services/features-selected.service";
import { MapOverlaysService } from "../../viewer-components/services/internal-services/overlay/map-overlays.service";
import { VIEWER_SERVICE } from "../../viewer-components/services/internal-services/viewer-services/inject-base-viewer.service";
import { ImageGalleryService } from "../../viewer-components/services/internal-services/image-gallery/image-gallery.service";
import { GeofencingLayerService, MapSearchLayerService } from "../services/layers";
import { OverlayRepositionService } from "../../viewer-components/services/internal-services/overlay-reposition.service";
import type { AgentDefinition } from "../../ai-components/chat/model/agent.types";
import type { ToolRendererConfig } from "../../ai-components/chat/model/tool-data.types";
import { DebugModeService } from "../../ai-components/chat/services/debug-mode.service";
import { FeatureFlagsService } from "../../viewer-components/services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../viewer-components/model/feature-flags";
import { MapCanvasService } from "../../viewer-components/services/internal-services/map-canvas.service";

/**
 * Compound container with multiple view to show compound results and other display features
 * Handle shared services like map service, studio viewer service
 * Toolbar service to handle map container toolbar actions etc
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-compound-main",
  templateUrl: "./compound-main.component.html",
  styleUrls: ["./compound-main.component.scss"],
  standalone: true,
  imports: [
    AsyncPipe,
    NgIf,
    MapDisplaySearchComponent,
    GridDisplaySearchComponent,
    AiViewDisplayComponent,
    PropertyAnalystDisplayComponent,
  ],
  providers: [
    { provide: VIEWER_SERVICE, useClass: CuratorViewerService },
    DebugModeService,
    ImageGalleryService,
    ToolbarService, // handle toolbar actions
    LoadingService, // show loading spinner inside the map
    MapService, // access to coremap function
    MapSearchLayerService,
    GeofencingLayerService,
    UrlStateService, // url state service
    DomService,
    FeaturesSelectedService, //  maybe to handle click on vector tiles for compounds and make some actions
    MapOverlaysService, // show overlays in the map with info
    OverlayRepositionService,
    MapCanvasService,
  ],
})
export class CompoundMainComponent implements OnInit {
  SearchDisplayMode = SearchDisplayMode;

  searchQueryService = inject(SearchQueryService);

  flagService = inject(FeatureFlagsService);

  mapCanvasService = inject(MapCanvasService);

  FeatureFlags = FeatureFlags;

  private overlayRepositionService = inject(OverlayRepositionService);

  private destroyRef = inject(DestroyRef);

  /** Agents available in the AI chat view. */
  @Input() agents: AgentDefinition[] = [];

  /** Optional custom tool renderers for inline tool call display. */
  @Input() toolRenderers: ToolRendererConfig[] = [];

  /** Agent ID for the property analyst panel — independent from the main chat session. */
  @Input() set propertyAgentId(id: string) {
    this._propertyAgentId.set(id);
  }

  get propertyAgentId(): string {
    return this._propertyAgentId();
  }

  private readonly _propertyAgentId = signal<string>("");

  /** Label for the property analyst agent shown in its panel header. */
  @Input() propertyAgentLabel: string = "Property Analyst";

  /** Single-entry agent list for the property panel — keeps it isolated from main agents.
   *  Looks up the full AgentDefinition from agents (preserving idleState and other fields).
   *  Falls back to a minimal definition if the agent is not found in the list. */
  protected readonly propertyAgents = computed<AgentDefinition[]>(() => {
    const id = this._propertyAgentId();
    if (!id) return [];
    const found = this.agents.find((a) => a.id === id);
    return [found ?? { id, label: this.propertyAgentLabel }];
  });

  protected readonly selectedAgentId = signal<string | null>(null);

  /** The active agent for the AI view — defaults to the first agent. */
  protected readonly activeAgent = computed(() => {
    const id = this.selectedAgentId();
    return this.agents.find((a) => a.id === id) ?? this.agents[0] ?? null;
  });

  ngOnInit(): void {
    this.searchQueryService.searchDisplayMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mode) => {
      if (mode !== SearchDisplayMode.MAP) {
        this.overlayRepositionService.closeAll();
      }
    });
  }

  /**
   * Handles the close event from the AI chat view.
   * Switches back to the grid display mode.
   */
  onAiChatClose(): void {
    this.searchQueryService.setSearchDisplayMode(SearchDisplayMode.GRID);
  }

  /** Handles the close event from the property analyst panel. */
  onPropertyChatClose(): void {
    this.searchQueryService.setSearchDisplayMode(SearchDisplayMode.GRID);
  }
}
